// Makes a recorder's WebM seekable before upload. Firefox's MediaRecorder
// writes the Segment and every Cluster with unknown sizes, a zero Duration,
// an empty SeekHead and no Cues, so players report an endless stream and
// can't seek in it. This rewrites such a file as SeekHead, Info (with the
// Duration), Tracks, Cues (one point per Cluster) and the Clusters, all
// sized; the blocks are copied unchanged. Pure, so it is tested in node.

const EBML = 0x1a45dfa3;
const SEGMENT = 0x18538067;
const SEEK_HEAD = 0x114d9b74;
const SEEK = 0x4dbb;
const SEEK_ID = 0x53ab;
const SEEK_POSITION = 0x53ac;
const INFO = 0x1549a966;
const TIMECODE_SCALE = 0x2ad7b1;
const DURATION = 0x4489;
const TRACKS = 0x1654ae6b;
const CLUSTER = 0x1f43b675;
const TIMECODE = 0xe7;
const SIMPLE_BLOCK = 0xa3;
const BLOCK_GROUP = 0xa0;
const BLOCK = 0xa1;
const CUES = 0x1c53bb6b;
const CUE_POINT = 0xbb;
const CUE_TIME = 0xb3;
const CUE_TRACK_POSITIONS = 0xb7;
const CUE_TRACK = 0xf7;
const CUE_CLUSTER_POSITION = 0xf1;

/** IDs that end an unknown-sized Cluster (the Segment's own children). */
const TOP_LEVEL = new Set([SEEK_HEAD, INFO, TRACKS, CLUSTER, CUES, 0x1254c367, 0x1043a770, 0x1941a469]);

class Malformed extends Error {}

interface Element {
  id: number;
  /** Where its ID starts. */
  at: number;
  /** Where its data starts and ends (the end of what holds it when unsized). */
  start: number;
  end: number;
  unknown: boolean;
}

function readId(b: Uint8Array, p: number): { id: number; length: number } {
  const first = b[p];
  if (first === undefined) throw new Malformed();
  const length = first & 0x80 ? 1 : first & 0x40 ? 2 : first & 0x20 ? 3 : first & 0x10 ? 4 : 0;
  if (length === 0 || p + length > b.length) throw new Malformed();
  let id = 0;
  for (let i = 0; i < length; i++) id = id * 256 + b[p + i]!;
  return { id, length };
}

function readSize(b: Uint8Array, p: number): { size: number; length: number; unknown: boolean } {
  const first = b[p];
  if (first === undefined || first === 0) throw new Malformed();
  let length = 1;
  while (!(first & (0x80 >> (length - 1)))) length++;
  if (p + length > b.length) throw new Malformed();
  let size = first & (0xff >> length);
  let ones = size === 0xff >> length;
  for (let i = 1; i < length; i++) {
    size = size * 256 + b[p + i]!;
    ones &&= b[p + i] === 0xff;
  }
  return { size, length, unknown: ones };
}

function readElement(b: Uint8Array, p: number, parentEnd: number): Element {
  const id = readId(b, p);
  const size = readSize(b, p + id.length);
  const start = p + id.length + size.length;
  const end = size.unknown ? parentEnd : start + size.size;
  if (end > parentEnd) throw new Malformed();
  return { id: id.id, at: p, start, end, unknown: size.unknown };
}

function readUint(b: Uint8Array, e: Element): number {
  let v = 0;
  for (let i = e.start; i < e.end; i++) v = v * 256 + b[i]!;
  return v;
}

function readFloat(b: Uint8Array, e: Element): number {
  const view = new DataView(b.buffer, b.byteOffset + e.start, e.end - e.start);
  return e.end - e.start === 4 ? view.getFloat32(0) : e.end - e.start === 8 ? view.getFloat64(0) : 0;
}

/** A block's track number and timestamp relative to its Cluster. */
function readBlock(b: Uint8Array, e: Element): { track: number; relative: number } {
  const track = readSize(b, e.start);
  const at = e.start + track.length;
  if (at + 3 > e.end) throw new Malformed();
  const relative = new DataView(b.buffer, b.byteOffset + at, 2).getInt16(0);
  return { track: track.size, relative };
}

// ------------------------------------------------------------- writing

function idBytes(id: number): number[] {
  const out: number[] = [];
  for (let v = id; v > 0; v = Math.floor(v / 256)) out.unshift(v % 256);
  return out;
}

function sizeBytes(size: number): number[] {
  let length = 1;
  // An all-ones value means "unknown", so it needs one more byte.
  while (size >= 2 ** (7 * length) - 1) length++;
  if (length > 8) throw new Malformed();
  const out: number[] = [];
  let v = size;
  for (let i = 0; i < length; i++) {
    out.unshift(v % 256);
    v = Math.floor(v / 256);
  }
  out[0]! |= 0x80 >> (length - 1);
  return out;
}

type Part = Uint8Array | number[];

function partsLength(parts: readonly Part[]): number {
  return parts.reduce((n, part) => n + part.length, 0);
}

function element(id: number, parts: readonly Part[]): Part[] {
  return [idBytes(id), sizeBytes(partsLength(parts)), ...parts];
}

/** An unsigned integer in eight bytes, so a position's size never depends on its value. */
function uint8Bytes(value: number): number[] {
  const out: number[] = [];
  for (let i = 0, v = value; i < 8; i++, v = Math.floor(v / 256)) out.unshift(v % 256);
  return out;
}

function float64Bytes(value: number): number[] {
  const bytes = new Uint8Array(8);
  new DataView(bytes.buffer).setFloat64(0, value);
  return [...bytes];
}

function concat(parts: readonly Part[]): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(partsLength(parts));
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}

// ------------------------------------------------------------- the file

interface Cluster {
  timecode: number;
  /** Its children, copied as they are. */
  body: Uint8Array;
}

/**
 * `bytes` rewritten seekable when it is a WebM without sizes, Duration or
 * Cues; the same bytes when it already has them; null when it isn't a WebM
 * this can read (then it goes as recorded).
 */
export function finalizeWebm(bytes: Uint8Array): Uint8Array | null {
  try {
    return finalize(bytes);
  } catch (error) {
    if (error instanceof Malformed || error instanceof RangeError) return null;
    throw error;
  }
}

function finalize(b: Uint8Array): Uint8Array | null {
  const header = readElement(b, 0, b.length);
  if (header.id !== EBML || header.unknown) return null;
  const segment = readElement(b, header.end, b.length);
  if (segment.id !== SEGMENT) return null;

  let info: Element | null = null;
  let tracks: Element | null = null;
  let hasCues = false;
  const clusters: Cluster[] = [];
  const times: number[] = [];
  let track: number | null = null;

  for (let p = segment.start; p < segment.end;) {
    const e = readElement(b, p, segment.end);
    if (e.id === CLUSTER) {
      let timecode: number | null = null;
      let end = e.end;
      const blocks: number[] = [];
      for (let q = e.start; q < e.end;) {
        // An unsized Cluster runs until the next Segment-level element.
        if (e.unknown && TOP_LEVEL.has(readId(b, q).id)) {
          end = q;
          break;
        }
        const child = readElement(b, q, e.end);
        if (child.unknown) throw new Malformed();
        if (child.id === TIMECODE) timecode = readUint(b, child);
        let block: Element | null = child.id === SIMPLE_BLOCK ? child : null;
        if (child.id === BLOCK_GROUP) {
          for (let r = child.start; r < child.end;) {
            const inner = readElement(b, r, child.end);
            if (inner.id === BLOCK) block = inner;
            r = inner.end;
          }
        }
        if (block) {
          const read = readBlock(b, block);
          track ??= read.track;
          blocks.push(read.relative);
        }
        q = child.end;
      }
      if (timecode === null) throw new Malformed();
      for (const relative of blocks) times.push(timecode + relative);
      clusters.push({ timecode, body: b.subarray(e.start, end) });
      p = end;
      continue;
    }
    if (e.unknown) throw new Malformed();
    if (e.id === INFO) info = e;
    else if (e.id === TRACKS) tracks = e;
    else if (e.id === CUES) hasCues = true;
    p = e.end;
  }
  if (!info || !tracks || clusters.length === 0 || track === null || times.length === 0) return null;

  // Info without its Duration; the timecode scale says what Duration counts in.
  const infoParts: Part[] = [];
  let scale = 1_000_000;
  let duration = 0;
  for (let p = info.start; p < info.end;) {
    const e = readElement(b, p, info.end);
    if (e.id === TIMECODE_SCALE) scale = readUint(b, e) || scale;
    if (e.id === DURATION) duration = readFloat(b, e);
    else infoParts.push(b.subarray(e.at, e.end));
    p = e.end;
  }
  if (!segment.unknown && hasCues && duration > 0) return b;

  // The last block plus one frame (the last step between blocks, else 20 ms).
  times.sort((x, y) => x - y);
  const last = times[times.length - 1]!;
  let frame = 0;
  for (let i = times.length - 1; i > 0 && frame <= 0; i--) frame = times[i]! - times[i - 1]!;
  if (frame <= 0) frame = 20_000_000 / scale;
  const infoElement = element(INFO, [...infoParts, ...element(DURATION, [float64Bytes(Math.max(0, last) + frame)])]);
  const tracksElement = [b.subarray(tracks.at, tracks.end)];
  const clusterElements = clusters.map((cluster) => element(CLUSTER, [cluster.body]));

  const seekHead = (positions: { info: number; tracks: number; cues: number }) =>
    element(SEEK_HEAD, [
      ...element(SEEK, [...element(SEEK_ID, [idBytes(INFO)]), ...element(SEEK_POSITION, [uint8Bytes(positions.info)])]),
      ...element(SEEK, [
        ...element(SEEK_ID, [idBytes(TRACKS)]),
        ...element(SEEK_POSITION, [uint8Bytes(positions.tracks)]),
      ]),
      ...element(SEEK, [...element(SEEK_ID, [idBytes(CUES)]), ...element(SEEK_POSITION, [uint8Bytes(positions.cues)])]),
    ]);
  const cues = (offsets: readonly number[]) =>
    element(
      CUES,
      clusters.flatMap((cluster, i) =>
        element(CUE_POINT, [
          ...element(CUE_TIME, [uint8Bytes(Math.max(0, cluster.timecode))]),
          ...element(CUE_TRACK_POSITIONS, [
            ...element(CUE_TRACK, [uint8Bytes(track)]),
            ...element(CUE_CLUSTER_POSITION, [uint8Bytes(offsets[i]!)]),
          ]),
        ]),
      ),
    );

  // Fixed-width numbers: the lengths are known before the positions are.
  const seekLength = partsLength(seekHead({ info: 0, tracks: 0, cues: 0 }));
  const infoAt = seekLength;
  const tracksAt = infoAt + partsLength(infoElement);
  const cuesAt = tracksAt + partsLength(tracksElement);
  let clusterAt = cuesAt + partsLength(cues(clusters.map(() => 0)));
  const offsets = clusterElements.map((parts) => {
    const at = clusterAt;
    clusterAt += partsLength(parts);
    return at;
  });
  const body: Part[] = [
    ...seekHead({ info: infoAt, tracks: tracksAt, cues: cuesAt }),
    ...infoElement,
    ...tracksElement,
    ...cues(offsets),
    ...clusterElements.flat(),
  ];
  return concat([b.subarray(0, header.end), ...element(SEGMENT, body)]);
}
